import { useSyncExternalStore } from 'react';
import type { OrderResponse } from 'api/generated/types.gen';

/*
 * Every product add and quantity change on the order goes through one queue,
 * whichever event, page or drawer it comes from. Each response replaces the
 * whole order, so overlapping requests could land out of order and bring back
 * removed rows or drop added ones.
 */

let tail: Promise<void> = Promise.resolve();
let inFlight = 0;
// Changes a stepper is holding back until it rests, not queued yet.
let held = 0;
// The order the last change answered with, while changes are still queued:
// app state only reaches the hooks' refs after a render, too late for the
// next change to be worked out from it.
let answered: { order: OrderResponse | undefined } | undefined;
const listeners = new Set<() => void>();
const flushers = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

/**
 * Runs a product change once those queued before it have finished. A change
 * that resolves the order it got back makes that the order the next one is
 * worked out from.
 */
export const enqueueProductChange = (
  change: () => Promise<{ order: OrderResponse | undefined } | void>
) => {
  inFlight += 1;
  notify();
  tail = tail
    .then(change)
    .then((result) => {
      if (result) answered = result;
    })
    .catch((error) => console.error('Product change error', error))
    .finally(() => {
      inFlight -= 1;
      if (inFlight === 0) answered = undefined;
      notify();
    });
  return tail;
};

/** The order to work the next change out from, given the caller's own copy. */
export const latestProductOrder = (fallback: OrderResponse | undefined) =>
  answered ? answered.order : fallback;

/**
 * Registers a way to send changes still held back (a stepper resting before
 * it sends), so `settleProductChanges` can send them at once.
 */
export const registerProductChangeFlush = (flush: () => void) => {
  flushers.add(flush);
  return () => {
    flushers.delete(flush);
  };
};

/**
 * Marks a change as held back until its stepper rests. Returns the release,
 * which the holder calls once the change is queued or dropped; calling it again
 * does nothing.
 */
export const holdProductChange = () => {
  held += 1;
  notify();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    held -= 1;
    notify();
  };
};

/**
 * Sends every held-back change and resolves once the queue has drained, so
 * checkout loads the order the buyer last asked for.
 */
export const settleProductChanges = async () => {
  flushers.forEach((flush) => flush());
  let waitedFor: Promise<void>;
  do {
    waitedFor = tail;
    await waitedFor;
  } while (waitedFor !== tail);
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Whether a product change is on its way to the backend. */
export const useProductChangesBusy = () =>
  useSyncExternalStore(
    subscribe,
    () => inFlight > 0,
    () => false
  );

/**
 * Whether a product change is held back or on its way, so the order on screen
 * may not be the one the backend holds. Unlike `useProductChangesBusy`, this
 * covers a stepper still resting, for actions that can't send held changes
 * first (paying has to start inside the click).
 */
export const useProductChangesSettling = () =>
  useSyncExternalStore(
    subscribe,
    () => held > 0 || inFlight > 0,
    () => false
  );
